import { useMemo, useState, type MouseEvent } from 'react';
import { cn } from '@/lib/utils';
import type { ViewState } from '@/types';
import { AvvikelsePills } from '@/features/avvikelser';
import type { DashboardPortfolio, PortfolioRow } from '../api';

interface Props {
  portfolio: DashboardPortfolio;
  loading?: boolean;
  language?: 'sv' | 'en';
  onNavigate?: (view: ViewState) => void;
}

type Tab = 'top' | 'exceptions';

function formatKr(value: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(value);
}

// ── Treemap strip ────────────────────────────────────────────────────────────
// One flex cell per site, weighted by price (priced sites share the available
// width; price-0 sites get a fixed minimal slice). When the portfolio is larger
// than `top`, a final hatched "övriga" cell absorbs the remaining contract value.

const MIN_FLEX = 0.4; // floor weight so 0-kr or tiny sites stay clickable
const LABEL_SHARE = 0.1; // cells holding >=10% of the strip get an inline label

interface TmTip {
  x: number;
  title: string;
  sub: string;
}

function Treemap({
  portfolio,
  language,
}: {
  portfolio: DashboardPortfolio;
  language: 'sv' | 'en';
}) {
  const [tip, setTip] = useState<TmTip | null>(null);

  const cells = useMemo(() => {
    const top = portfolio.top;
    const topSum = top.reduce((s, r) => s + r.price, 0);

    const rest = Math.max(portfolio.total_count - top.length, 0);
    const restContract = Math.max(portfolio.total_contract - topSum, 0);

    const items = top.map((row) => ({
      row,
      flex: Math.max(row.price, MIN_FLEX),
      flagged: row.flags.length > 0,
    }));
    const flexTotal =
      items.reduce((s, i) => s + i.flex, 0) +
      (rest > 0 ? Math.max(restContract, MIN_FLEX) : 0);

    return { items, rest, restContract, flexTotal };
  }, [portfolio]);

  const noPrice = language === 'sv' ? 'pris saknas' : 'no price';

  const showTip = (e: MouseEvent<HTMLElement>, title: string, sub: string) => {
    const host = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    const cell = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max(cell.left - host.left + cell.width / 2, 70), host.width - 70);
    setTip({ x, title, sub });
  };

  return (
    <div className="relative">
      <div className="flex w-full gap-px rounded overflow-hidden" style={{ height: 64 }}>
        {cells.items.map(({ row, flex, flagged }) => {
          const share = cells.flexTotal > 0 ? flex / cells.flexTotal : 0;
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => {
                window.location.hash = `#/brfs/${row.id}`;
              }}
              onMouseEnter={(e) =>
                showTip(e, row.name, row.price > 0 ? `${formatKr(row.price)}/mån` : noPrice)
              }
              onMouseLeave={() => setTip(null)}
              className={cn(
                'cmd-tm-cell h-full min-w-[3px] overflow-hidden',
                flagged ? 'cmd-tm-warn' : 'cmd-tm-ok',
              )}
              style={{ flexGrow: flex, flexBasis: 0 }}
            >
              {share >= LABEL_SHARE && (
                <span className="pointer-events-none flex h-full flex-col items-start justify-end px-1.5 pb-1 text-left">
                  <span className="block w-full truncate font-mono text-[9px] leading-tight text-[#2E4640]">
                    {row.name.replace(/^brf\s+/i, '')}
                  </span>
                  <span
                    className="block font-mono tabular-nums text-[9px] leading-tight"
                    style={{ color: flagged ? '#B97E1F' : '#1B7F4D' }}
                  >
                    {row.price > 0 ? formatKr(row.price) : '—'}
                  </span>
                </span>
              )}
            </button>
          );
        })}

        {cells.rest > 0 && (
          <button
            type="button"
            onClick={() => {
              window.location.hash = '#/brfs';
            }}
            onMouseEnter={(e) =>
              showTip(
                e,
                `${cells.rest} ${language === 'sv' ? 'övriga objekt' : 'other sites'}`,
                `${formatKr(cells.restContract)}/mån`,
              )
            }
            onMouseLeave={() => setTip(null)}
            className="cmd-tm-cell cmd-tm-rest h-full min-w-[8px]"
            style={{ flexGrow: Math.max(cells.restContract, MIN_FLEX), flexBasis: 0 }}
          />
        )}
      </div>

      {tip && (
        <div
          className="pointer-events-none absolute z-10 rounded px-2 py-1.5"
          style={{
            left: tip.x,
            top: -6,
            transform: 'translate(-50%, -100%)',
            background: '#FFFFFF',
            border: '1px solid rgba(160,123,82,0.5)',
            boxShadow: '0 2px 8px rgba(7,51,49,0.10)',
            whiteSpace: 'nowrap',
          }}
        >
          <p className="text-xs font-mono text-[#073331]">{tip.title}</p>
          <p className="cmd-label">{tip.sub}</p>
        </div>
      )}
    </div>
  );
}

// ── Shared row ───────────────────────────────────────────────────────────────

function renderRow(row: PortfolioRow, totalContract: number) {
  const share = totalContract > 0 ? row.price / totalContract : 0;
  const flagged = row.flags.length > 0;

  return (
    <tr
      key={row.id}
      onClick={() => {
        window.location.hash = `#/brfs/${row.id}`;
      }}
      className="border-b border-[#073331]/[0.06] cursor-pointer hover:bg-[#073331]/[0.04] transition-colors group align-middle"
      style={{ minHeight: 32 }}
    >
      {/* Name */}
      <td className="py-1 pr-2">
        <span className="text-xs font-mono text-[#2E4640] group-hover:text-[#073331] transition-colors truncate block max-w-[160px]">
          {row.name}
        </span>
      </td>

      {/* Price + share bar */}
      <td className="py-1 px-2 text-right">
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-xs font-mono tabular-nums text-[#073331]">
            {row.price > 0 ? formatKr(row.price) : <span style={{ color: '#B97E1F' }}>—</span>}
          </span>
          <div
            className="w-full max-w-[80px] h-0.5 rounded-full overflow-hidden"
            style={{ background: 'rgba(7,51,49,0.08)' }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${(share * 100).toFixed(1)}%`,
                background: row.price > 0 ? 'rgba(160,123,82,0.6)' : 'rgba(185,126,31,0.4)',
              }}
            />
          </div>
        </div>
      </td>

      {/* Jobs count */}
      <td className="py-1 px-2 text-center">
        <span className="text-xs font-mono tabular-nums text-[#607C6E] group-hover:text-[#2E4640] transition-colors">
          {row.jobs_30d}
        </span>
      </td>

      {/* Status / flags */}
      <td className="py-1 pl-2 text-right">
        {flagged ? (
          <AvvikelsePills siteId={row.id} flags={row.flags} />
        ) : (
          <span
            className="cmd-label px-1.5 py-0.5 rounded text-[8px]"
            style={{ background: 'rgba(27,127,77,0.10)', color: '#1B7F4D' }}
          >
            OK
          </span>
        )}
      </td>
    </tr>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────────

export function PortfolioTable({ portfolio, loading, language = 'sv', onNavigate }: Props) {
  const [tab, setTab] = useState<Tab>('top');

  if (loading) {
    return (
      <div className="cmd-panel cmd-panel-inner rounded p-4 h-full flex flex-col">
        <div className="flex items-center justify-between mb-3 shrink-0">
          <span className="cmd-label">PORTFÖLJ — AKTIVA OBJEKT</span>
        </div>
        <div className="h-16 mb-3 rounded animate-pulse" style={{ background: 'rgba(7,51,49,0.05)' }} />
        <div className="flex-1 space-y-1 min-h-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-8 rounded animate-pulse"
              style={{ background: 'rgba(7,51,49,0.05)' }}
            />
          ))}
        </div>
      </div>
    );
  }

  const { total_count, total_contract, ok_count, top, exceptions } = portfolio;
  const rest = Math.max(total_count - top.length, 0);
  const rows = tab === 'top' ? top : exceptions;

  return (
    <div className="cmd-panel cmd-panel-inner rounded p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <span className="cmd-label">PORTFÖLJ — AKTIVA OBJEKT</span>
        <span className="cmd-label text-[#8A6A47] font-mono">
          {total_count} OBJEKT · {formatKr(total_contract)}/MÅN
        </span>
      </div>

      {/* Treemap strip */}
      <div className="mb-3 shrink-0">
        <Treemap portfolio={portfolio} language={language} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 mb-2 shrink-0 border-b border-[#073331]/10">
        <button
          type="button"
          onClick={() => setTab('top')}
          className="cmd-label pb-1.5 -mb-px transition-colors"
          style={{
            color: tab === 'top' ? '#8A6A47' : '#607C6E',
            borderBottom: tab === 'top' ? '1.5px solid #8A6A47' : '1.5px solid transparent',
          }}
        >
          {language === 'sv' ? 'STÖRSTA' : 'LARGEST'}
        </button>
        <button
          type="button"
          onClick={() => setTab('exceptions')}
          className="cmd-label pb-1.5 -mb-px transition-colors"
          style={{
            color: tab === 'exceptions' ? '#8A6A47' : '#607C6E',
            borderBottom: tab === 'exceptions' ? '1.5px solid #8A6A47' : '1.5px solid transparent',
          }}
        >
          {language === 'sv' ? 'AVVIKELSER' : 'EXCEPTIONS'} ({exceptions.length})
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto cmd-scroll min-h-0">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="cmd-label text-left py-1.5 pr-2 font-normal border-b border-[#073331]/10">
                {language === 'sv' ? 'OBJEKT' : 'SITE'}
              </th>
              <th className="cmd-label text-right py-1.5 px-2 font-normal border-b border-[#073331]/10 w-28">
                KR/MÅN
              </th>
              <th className="cmd-label text-center py-1.5 px-2 font-normal border-b border-[#073331]/10 w-8">
                30D
              </th>
              <th className="cmd-label text-right py-1.5 pl-2 font-normal border-b border-[#073331]/10 w-28">
                STATUS
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => renderRow(row, total_contract))}
          </tbody>
        </table>

        {/* Empty-state for exceptions */}
        {tab === 'exceptions' && exceptions.length === 0 && (
          <p className="text-xs font-mono text-[#1B7F4D] py-3">
            {language === 'sv' ? 'Inga avvikelser.' : 'No exceptions.'}
          </p>
        )}
      </div>

      {/* Footer */}
      {tab === 'top' && rest > 0 && (
        <button
          type="button"
          onClick={() => onNavigate?.('brfs')}
          className="cmd-label text-left pt-2 mt-1 shrink-0 text-[#607C6E] hover:text-[#8A6A47] transition-colors border-t border-[#073331]/[0.06]"
        >
          …{language === 'sv' ? 'och' : 'and'} {rest} {language === 'sv' ? 'till' : 'more'} →
        </button>
      )}
      {tab === 'exceptions' && (
        <div className="flex items-center gap-1.5 pt-2 mt-1 shrink-0 border-t border-[#073331]/[0.06]">
          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#1B7F4D' }} />
          <span className="cmd-label text-[#607C6E]">
            {ok_count} {language === 'sv' ? 'objekt OK' : 'sites OK'}
          </span>
        </div>
      )}
    </div>
  );
}
